import axios from "axios";
import ModuleModal from "./appresourceModel.vue";
import TreeNode from "./treenode.vue";

export default {
  name: "AppResource",

  components: {
    ModuleModal,
    TreeNode
  },

  data() {
    return {
      baseUrl: process.env.VUE_APP_Service_URL,
      idToken: this.$store.getters.getIdToken,
      tree: [],
      filtertreenode: [],
      search: "",
      loading: false,
      error: null,
      selectedResource: null,
      detailLoading: false,
      detailError: null,
      expandedIds: new Set(),
    };
  },

  computed: {

    headers() {
      return { Authorization: `Bearer ${this.idToken}` };
    },

    filteredTree() {
      if (!this.search)
        return this.tree;

      return this.tree
        .map(folder => ({
          ...folder,
          children: (folder.children || []).filter(child =>
            child.name.toLowerCase().includes(this.search.toLowerCase())
          )
        }))
        .filter(
          folder =>
            (folder.children && folder.children.length) ||
            folder.name.toLowerCase().includes(this.search.toLowerCase())
        );
    },

    flatParentOptions() {
      const options = [];

      const walk = (nodes, prefix) => {
        nodes.forEach(node => {
          const label = prefix ? `${prefix} → ${node.name}` : node.name;
          options.push({
            id: node.id,
            label
          });

          if (node.children && node.children.length) {
            walk(node.children, label);
          }
        });
      };

      walk(this.tree, "");
      return options;
    }
  },

  created() {
    this.fetchResources();
  },

  methods: {
    toggle(folder) {
      folder.open = !folder.open;
      if (folder.open) this.expandedIds.add(folder.id);
      else this.expandedIds.delete(folder.id);
    },

    expandAll() {
      const setOpen = (nodes, val) => {
        nodes.forEach(node => {
          node.open = val;
          if (val) this.expandedIds.add(node.id);
          else this.expandedIds.delete(node.id);
          if (node.children && node.children.length) {
            setOpen(node.children, val);
          }
        });
      };
      setOpen(this.tree, true);
    },

    collapseAll() {
      const setOpen = (nodes, val) => {
        nodes.forEach(node => {
          node.open = val;
          if (val) this.expandedIds.add(node.id);
          else this.expandedIds.delete(node.id);
          if (node.children && node.children.length) {
            setOpen(node.children, val);
          }
        });
      };
      setOpen(this.tree, false);
    },

    fetchResources() {
      this.loading = true;
      this.error = null;
      axios
        .get(`${this.baseUrl}/admin/appresource/getall`, {
          headers: this.headers
        })
        .then(response => {
          const entities = response.data.entities || response.data || [];
          this.tree = this.buildTree(entities);
        })
        .catch(error => {
          console.error("Failed to load app resources:", error);
          this.error = "Unable to load resources. Please try again.";
        })
        .finally(() => {
          this.loading = false;
        });
    },

    buildTree(entities) {
      const norm = v => (v || "").toString().trim().toLowerCase();

      const idMap = new Map(entities.map(e => [norm(e.id), e]));
      const childrenOf = {};

      entities.forEach(e => {
        const parentKey = norm(e.parentResourceId);
        if (parentKey && idMap.has(parentKey)) {
          const parent = idMap.get(parentKey);
          if (!childrenOf[parent.id]) childrenOf[parent.id] = [];
          childrenOf[parent.id].push(e);
        }
      });

      const buildNode = e => ({
        id: e.id,
        name: e.resourceName,
        moduleId: e.moduleId,
        resourceCode: e.resourceCode,
        open: this.expandedIds.has(e.id),
        checked: false,
        children: (childrenOf[e.id] || [])
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map(buildNode)
      });

      const roots = entities
        .filter(e => {
          const parentKey = norm(e.parentResourceId);
          return !parentKey || parentKey == " " || parentKey == null;
        })
        .sort((a, b) => a.displayOrder - b.displayOrder);
      console.log(roots);

      return roots.map(buildNode);
    },

    getResourceById(id) {
      return axios
        .get(`${this.baseUrl}/admin/appresource/getbyid`, {
          params: { id },
          headers: this.headers
        })
        .then(response => response.data)
        .catch(error => {
          console.error("Failed to load resource", id, error);
          throw error;
        });
    },

    async selectNode(node) {
      if (node.children && node.children.length) {
        this.toggle(node);
      }

      this.detailLoading = true;
      this.detailError = null;

      try {
        this.selectedResource = await this.getResourceById(node.id);
      } catch (err) {
        this.detailError = "Unable to load resource details.";
        this.selectedResource = null;
      } finally {
        this.detailLoading = false;
      }
    },

    saveChanges() {
      const checkedResourceIds = [];

      const walk = nodes => {
        nodes.forEach(node => {
          if (node.checked) checkedResourceIds.push(node.id);
          if (node.children && node.children.length) walk(node.children);
        });
      };

      walk(this.tree);

      console.log("Checked resource ids:", checkedResourceIds);

    },
    removeNodeById(nodes, id) {
      const index = nodes.findIndex(n => n.id === id);
      if (index !== -1) {
        nodes.splice(index, 1);
        return true;
      }
      for (const node of nodes) {
        if (node.children && node.children.length) {
          if (this.removeNodeById(node.children, id)) return true;
        }
      }
      return false;
    },

    async deleteFolder(node) {
      if (!confirm(`Are you sure you want to delete "${node.name}" node?`)) return;

      this.error = null;

      try {
        await axios.delete(`${this.baseUrl}/admin/appresource/delete`, {
          params: { id: node.id },
          headers: this.headers
        });
        this.removeNodeById(this.tree, node.id);

        if (this.selectedResource && this.selectedResource.id === node.id) {
          this.selectedResource = null;
        }
      } catch (error) {
        console.error("Failed to delete resource:", error);
        this.error = "Unable to delete resource. Please try again.";
      }
    },

    addFolder(parentNode) {
      this.$refs.moduleModal.openModuleModal(null, parentNode);
    },

    async editFolder(node) {
      try {
        const fullResource = await this.getResourceById(node.id);
        this.$refs.moduleModal.openModuleModal(fullResource);
      } catch (err) {
        console.error("Failed to load resource for edit:", err);
        this.error = "Unable to load resource for editing.";
      }
    },
    toggleChecked({ node, checked }) {
      node.checked = checked;
    },

    onResourceSaved() {
      this.fetchResources();
    },
  }
};